import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Copy, Download, FileText, Link2, Pencil, Share2, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { citationLabel, citationStyle, confidenceClass, confidenceLabel, sourceCategory } from "../lib/citations";

// Kept in sync with AppealAnalysisCard.jsx's wording — same evidence-first reasoning,
// duplicated here because this is a separate module. See that file's comment for why.
const VERDICT_STYLES = {
    strong: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0", label: "Well-evidenced" },
    moderate: { bg: "#FFFBEB", text: "#B45309", border: "#FDE68A", label: "Partly evidenced" },
    weak: { bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA", label: "Little evidence found" },
    insufficient: { bg: "#F1F5F9", text: "#475569", border: "#CBD5E1", label: "No evidence found" },
};

const STRENGTH_STYLES = {
    high: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0", label: "Directly cited" },
    medium: { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", label: "Partially cited" },
    low: { bg: "#FFFBEB", text: "#B45309", border: "#FDE68A", label: "Weakly cited" },
};

const PRECEDENT_STYLES = {
    successful: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0", label: "Past success" },
    failed: { bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA", label: "Past failure" },
    none: { bg: "#F8FAFC", text: "#475569", border: "#E2E8F0", label: "No precedent" },
};

function buildLineDiff(leftText = "", rightText = "") {
    const left = leftText.split(/\r?\n/);
    const right = rightText.split(/\r?\n/);
    const rows = Array.from({ length: left.length + 1 }, () => Array(right.length + 1).fill(0));
    for (let i = left.length - 1; i >= 0; i -= 1) {
        for (let j = right.length - 1; j >= 0; j -= 1) {
            rows[i][j] = left[i] === right[j] ? rows[i + 1][j + 1] + 1 : Math.max(rows[i + 1][j], rows[i][j + 1]);
        }
    }

    const diff = [];
    let i = 0;
    let j = 0;
    while (i < left.length || j < right.length) {
        if (i < left.length && j < right.length && left[i] === right[j]) {
            diff.push({ type: "same", left: left[i], right: right[j] });
            i += 1;
            j += 1;
        } else if (j < right.length && (i === left.length || rows[i][j + 1] >= rows[i + 1]?.[j])) {
            diff.push({ type: "added", left: "", right: right[j] });
            j += 1;
        } else if (i < left.length) {
            diff.push({ type: "removed", left: left[i], right: "" });
            i += 1;
        }
    }
    return diff;
}

function Pill({ style, dataTestid }) {
    return (
        <span
            data-testid={dataTestid}
            className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded"
            style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
        >
            {style.label}
        </span>
    );
}

export default function AppealDraftView({ draft }) {
    const { user } = useAuth();
    const [currentDraft, setCurrentDraft] = useState(draft);
    const [versions, setVersions] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [editLetter, setEditLetter] = useState("");
    const [changeSummary, setChangeSummary] = useState("");
    const [compareLeftId, setCompareLeftId] = useState("");
    const [compareRightId, setCompareRightId] = useState("");
    const [includeSignature, setIncludeSignature] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareTokens, setShareTokens] = useState([]);
    const [shareEmail, setShareEmail] = useState("");
    const [shareNote, setShareNote] = useState("");
    const [shareDays, setShareDays] = useState(14);
    const isOwner = draft?.owner_id === user?.id;
    const isEstateAdmin = user?.role === "estate_admin";
    const isAgencyViewer = user?.role === "agency_viewer";
    const canEditDraft = isOwner;
    const canDownloadPack = isOwner;
    const canManageReviewLinks = isOwner || isEstateAdmin;

    useEffect(() => {
        setCurrentDraft(draft);
        if (draft?.id) {
            if (canEditDraft || canDownloadPack) {
                api.get(`/parking/drafts/${draft.id}/versions`)
                .then((res) => {
                    const nextVersions = res.data || [];
                    setVersions(nextVersions);
                    if (nextVersions.length > 1) {
                        setCompareLeftId(nextVersions[0].id);
                        setCompareRightId((nextVersions[nextVersions.length - 1] || nextVersions[0]).id);
                    }
                })
                .catch(() => setVersions([]));
            } else {
                setVersions([]);
            }
            if (canManageReviewLinks) {
                api.get(`/parking/drafts/${draft.id}/share-tokens`)
                .then((res) => setShareTokens(res.data || []))
                .catch(() => setShareTokens([]));
            } else {
                setShareTokens([]);
            }
        }
    }, [draft, canEditDraft, canDownloadPack, canManageReviewLinks]);

    if (!currentDraft) return null;
    const analysis = currentDraft.analysis || null;
    const selectedIds = new Set(currentDraft.selected_ground_ids || []);
    const selectedGrounds = (analysis?.grounds || []).filter((g) => selectedIds.has(g.id));
    const compareLeft = versions.find((v) => v.id === compareLeftId) || versions[0];
    const compareRight = versions.find((v) => v.id === compareRightId) || versions[versions.length - 1];
    const comparisonRows = compareLeft && compareRight ? buildLineDiff(compareLeft.appeal_letter || "", compareRight.appeal_letter || "") : [];
    const addedLines = comparisonRows.filter((row) => row.type === "added").length;
    const removedLines = comparisonRows.filter((row) => row.type === "removed").length;

    const copyLetter = async () => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(currentDraft.appeal_letter || "");
            } else {
                throw new Error("Clipboard API unavailable");
            }
            toast.success("Letter copied to clipboard");
        } catch (err) {
            try {
                const ta = document.createElement("textarea");
                ta.value = currentDraft.appeal_letter || "";
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                ta.remove();
                toast.success("Letter copied to clipboard");
            } catch (e) {
                toast.error("Could not copy letter — clipboard not available");
            }
        }
    };

    const downloadPack = async () => {
        try {
            const res = await api.get(`/parking/drafts/${currentDraft.id}/evidence-pack`, {
                params: { include_signature: includeSignature },
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `evidence-pack-${currentDraft.intake?.pcn_reference || "pcn"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Evidence pack downloaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not generate evidence pack");
        }
    };

    const openEdit = () => {
        setEditLetter(currentDraft.appeal_letter || "");
        setChangeSummary("");
        setEditOpen(true);
    };

    const saveVersion = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post(`/parking/drafts/${currentDraft.id}/versions`, {
                appeal_letter: editLetter,
                change_summary: changeSummary,
            });
            setCurrentDraft(res.data);
            const versionsRes = await api.get(`/parking/drafts/${res.data.id}/versions`);
            setVersions(versionsRes.data || []);
            setEditOpen(false);
            toast.success("Draft version saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save draft version");
        }
    };

    const absoluteShareUrl = (share) => {
        if (!share?.share_url) return "";
        if (/^https?:\/\//i.test(share.share_url)) return share.share_url;
        return `${window.location.origin}${share.share_url}`;
    };

    const copyShareUrl = async (share) => {
        const url = absoluteShareUrl(share);
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Review link copied");
        } catch (err) {
            toast.error("Could not copy review link");
        }
    };

    const createShareToken = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post(`/parking/drafts/${currentDraft.id}/share-token`, {
                recipient_email: shareEmail || null,
                note: shareNote || null,
                expires_in_days: Number(shareDays) || 14,
            });
            setShareTokens((items) => [res.data, ...items]);
            setShareEmail("");
            setShareNote("");
            await copyShareUrl(res.data);
            toast.success(shareEmail ? "Review link created and email queued" : "Review link created");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create review link");
        }
    };

    const revokeShareToken = async (share) => {
        try {
            const res = await api.post(`/parking/drafts/${currentDraft.id}/share-tokens/${share.id}/revoke`);
            setShareTokens((items) => items.map((item) => (item.id === share.id ? res.data : item)));
            toast.success("Review link revoked");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not revoke review link");
        }
    };

    return (
        <div className="space-y-6" data-testid="appeal-draft-view">
            {analysis && (
                <section className="border border-slate-200 bg-white rounded-lg p-5" data-testid="draft-analysis">
                    <div className="flex items-end justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500">Evidence review</div>
                            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-1">How this matches the record</h2>
                        </div>
                        <div className="text-right">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Evidence match</div>
                            <div className="font-heading text-3xl font-bold text-slate-900 leading-none" data-testid="draft-score">
                                {analysis.overall_score}<span className="text-base text-slate-500">/100</span>
                            </div>
                            <div className="mt-2">
                                <Pill style={VERDICT_STYLES[analysis.verdict] || VERDICT_STYLES.insufficient} dataTestid="draft-verdict" />
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full"
                            style={{
                                width: `${Math.max(2, Math.min(100, analysis.overall_score || 0))}%`,
                                backgroundColor: (VERDICT_STYLES[analysis.verdict] || VERDICT_STYLES.insufficient).text,
                                transition: "width 240ms ease-out",
                            }}
                        />
                    </div>
                    {analysis.analysis_disclaimer && (
                        <p className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-3" data-testid="draft-analysis-disclaimer">
                            {analysis.analysis_disclaimer}
                        </p>
                    )}
                </section>
            )}

            {selectedGrounds.length > 0 && (
                <section data-testid="draft-grounds">
                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Selected grounds ({selectedGrounds.length})</div>
                    <div className="space-y-3">
                        {selectedGrounds.map((g) => {
                            const s = STRENGTH_STYLES[g.strength] || STRENGTH_STYLES.medium;
                            const p = PRECEDENT_STYLES[g.precedent] || PRECEDENT_STYLES.none;
                            return (
                                <div key={g.id} className="border border-slate-200 bg-white rounded-lg p-4" data-testid={`draft-ground-${g.id}`}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-mono text-slate-500">{g.id}</span>
                                        <h3 className="font-heading text-sm font-semibold text-slate-900">{g.title}</h3>
                                        <Pill style={s} />
                                        <Pill style={p} />
                                    </div>
                                    <p className="text-sm text-slate-700 mt-1.5">{g.description}</p>
                                    {g.rationale && (
                                        <p className="text-xs text-slate-500 mt-1 italic">Why this applies: {g.rationale}</p>
                                    )}
                                    {g.rule_citations?.length > 0 && (
                                        <ul className="mt-2 space-y-1">
                                            {g.rule_citations.map((c, i) => {
                                                const label = citationLabel(c);
                                                const cs = citationStyle(c);
                                                return (
                                                    <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                                                        <span
                                                            className="inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap"
                                                            style={{ backgroundColor: cs.bg, color: cs.text, borderColor: cs.border }}
                                                        >
                                                            {label}
                                                        </span>
                                                        <span className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap ${confidenceClass(c)}`}>
                                                            {confidenceLabel(c)}
                                                        </span>
                                                        <span>
                                                            {c.document_title && <strong>{c.document_title}: </strong>}
                                                            <span className="text-slate-500">{sourceCategory(c)}. </span>
                                                            {c.snippet}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            <section className="border border-slate-200 bg-white rounded-lg p-5" data-testid="draft-letter-section">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                            <FileText size={12} /> Draft appeal letter
                        </div>
                        <h2 className="font-heading text-lg font-semibold text-slate-900 mt-1">Ready for review</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {canEditDraft && (
                            <Button variant="outline" onClick={openEdit} data-testid="draft-edit-button">
                                <Pencil size={12} className="mr-1.5" /> Edit
                            </Button>
                        )}
                        <Button variant="outline" onClick={copyLetter} data-testid="draft-copy-button">
                            <Copy size={12} className="mr-1.5" /> Copy
                        </Button>
                        {canManageReviewLinks && (
                            <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="draft-share-button">
                                <Share2 size={12} className="mr-1.5" /> Review link
                            </Button>
                        )}
                        {canDownloadPack && (
                            <Button onClick={downloadPack} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="draft-download-button">
                                <Download size={12} className="mr-1.5" /> Evidence pack for v{currentDraft.version}
                            </Button>
                        )}
                    </div>
                </div>
                <div className="text-xs text-slate-500 mb-3" data-testid="draft-version-label">
                    Selected version {currentDraft.version}.
                    {canDownloadPack ? " Evidence pack downloads use this selected version." : ""}
                    {currentDraft.change_summary ? ` Change: ${currentDraft.change_summary}` : ""}
                </div>
                {isAgencyViewer && (
                    <div className="mb-3 text-xs text-slate-500 border border-slate-200 bg-slate-50 rounded-md p-3" data-testid="draft-readonly-agency-note">
                        Agency viewers have read-only access to this shared case. Resident draft editing, evidence-pack generation, and professional review links remain restricted to the resident or estate admin workflows.
                    </div>
                )}
                {canDownloadPack && (
                    <label className="mb-3 inline-flex items-center gap-2 text-sm text-slate-700" data-testid="signature-section-toggle">
                        <input
                            type="checkbox"
                            checked={includeSignature}
                            onChange={(e) => setIncludeSignature(e.target.checked)}
                        />
                        Include optional resident signature page in the PDF
                    </label>
                )}
                <pre className="whitespace-pre-wrap text-sm text-slate-800 font-body" data-testid="draft-letter-text">{currentDraft.appeal_letter}</pre>
            </section>

            {versions.length > 1 && (
                <section className="border border-slate-200 bg-white rounded-lg p-5 space-y-5" data-testid="draft-versions-section">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Draft versions</div>
                        <div className="flex flex-wrap gap-2">
                            {versions.map((v) => (
                                <Button key={v.id} variant={v.id === currentDraft.id ? "default" : "outline"} size="sm" onClick={() => setCurrentDraft(v)} data-testid={`draft-version-${v.version}`}>
                                    v{v.version}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-slate-200 pt-5" data-testid="draft-version-comparison">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500">Compare versions</div>
                                <div className="text-sm text-slate-500 mt-1">
                                    Review wording changes before copying the letter or downloading the selected evidence pack.
                                </div>
                            </div>
                            <div className="text-xs text-slate-500" data-testid="draft-comparison-summary">
                                {addedLines} added lines · {removedLines} removed lines
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                            <div className="space-y-1.5">
                                <Label>From</Label>
                                <select
                                    value={compareLeft?.id || ""}
                                    onChange={(e) => setCompareLeftId(e.target.value)}
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                                    data-testid="draft-compare-left-select"
                                >
                                    {versions.map((v) => (
                                        <option key={v.id} value={v.id}>v{v.version}{v.change_summary ? ` - ${v.change_summary}` : ""}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>To</Label>
                                <select
                                    value={compareRight?.id || ""}
                                    onChange={(e) => setCompareRightId(e.target.value)}
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                                    data-testid="draft-compare-right-select"
                                >
                                    {versions.map((v) => (
                                        <option key={v.id} value={v.id}>v{v.version}{v.change_summary ? ` - ${v.change_summary}` : ""}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-xs" data-testid="draft-comparison-grid">
                            <div className="border border-slate-200 rounded-md overflow-hidden">
                                <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-700">v{compareLeft?.version}</div>
                                <div className="divide-y divide-slate-100">
                                    {comparisonRows.map((row, idx) => (
                                        <div
                                            key={`left-${idx}`}
                                            className={`px-3 py-1.5 min-h-[30px] whitespace-pre-wrap ${row.type === "removed" ? "bg-red-50 text-red-900" : "text-slate-700"}`}
                                            data-testid={`draft-comparison-left-${idx}`}
                                        >
                                            {row.left || " "}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="border border-slate-200 rounded-md overflow-hidden">
                                <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-700">v{compareRight?.version}</div>
                                <div className="divide-y divide-slate-100">
                                    {comparisonRows.map((row, idx) => (
                                        <div
                                            key={`right-${idx}`}
                                            className={`px-3 py-1.5 min-h-[30px] whitespace-pre-wrap ${row.type === "added" ? "bg-emerald-50 text-emerald-900" : "text-slate-700"}`}
                                            data-testid={`draft-comparison-right-${idx}`}
                                        >
                                            {row.right || " "}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {currentDraft.citations?.length > 0 && (
                <section className="border border-slate-200 bg-white rounded-lg p-5" data-testid="draft-citations-section">
                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Citations ({currentDraft.citations.length})</div>
                    <ul className="space-y-3">
                        {currentDraft.citations.map((c, i) => {
                            const label = citationLabel(c);
                            const cs = citationStyle(c);
                            return (
                                <li key={i} className="text-sm" data-testid={`draft-citation-${i}`}>
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span
                                            className="inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border"
                                            style={{ backgroundColor: cs.bg, color: cs.text, borderColor: cs.border }}
                                        >
                                            {label}
                                        </span>
                                        <span className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${confidenceClass(c)}`}>
                                            {confidenceLabel(c)}
                                        </span>
                                        <span className="text-xs text-slate-500">{sourceCategory(c)}</span>
                                    </div>
                                    {c.document_title && <strong className="text-slate-800">{c.document_title}: </strong>}
                                    <span className="text-slate-700">{c.snippet}</span>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent data-testid="draft-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit appeal draft</DialogTitle>
                        <DialogDescription>
                            Save changes as a new version. The original AI draft remains available for audit and comparison.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveVersion} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Change summary</Label>
                            <Input value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} placeholder="e.g. Added photos and clarified bay allocation" data-testid="draft-change-summary-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Letter</Label>
                            <Textarea rows={14} value={editLetter} onChange={(e) => setEditLetter(e.target.value)} required data-testid="draft-edit-letter-input" />
                        </div>
                        <DialogFooter>
                            <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="draft-save-version-button">
                                Save new version
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
                <DialogContent data-testid="draft-share-dialog">
                    <DialogHeader>
                        <DialogTitle>Share draft for professional review</DialogTitle>
                        <DialogDescription>
                            Create a time-limited read-only link for this selected draft version. It does not grant portal access, case access, or document-download access.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={createShareToken} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="space-y-1.5 md:col-span-2">
                                <Label>Reviewer email</Label>
                                <Input
                                    type="email"
                                    value={shareEmail}
                                    onChange={(e) => setShareEmail(e.target.value)}
                                    placeholder="optional@example.com"
                                    data-testid="draft-share-email-input"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Expires in days</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={shareDays}
                                    onChange={(e) => setShareDays(e.target.value)}
                                    data-testid="draft-share-days-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Reviewer note</Label>
                            <Textarea
                                rows={3}
                                value={shareNote}
                                onChange={(e) => setShareNote(e.target.value)}
                                placeholder="Optional context for the advisor"
                                data-testid="draft-share-note-input"
                            />
                        </div>
                        <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
                            <ShieldCheck size={16} className="text-emerald-700 shrink-0 mt-0.5" />
                            <span>The shared page shows the appeal draft, selected grounds, citations, and PCN details in the draft. Private uploaded evidence files stay behind resident authentication.</span>
                        </div>
                        <DialogFooter>
                            <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="draft-create-share-button">
                                <Link2 size={12} className="mr-1.5" /> Create review link
                            </Button>
                        </DialogFooter>
                    </form>

                    {shareTokens.length > 0 && (
                        <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="draft-share-token-list">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Existing review links</div>
                            {shareTokens.map((share) => {
                                const revoked = Boolean(share.revoked_at);
                                return (
                                    <div key={share.id} className="border border-slate-200 rounded-md p-3 text-sm">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="font-semibold text-slate-800">
                                                    {share.recipient_email || "Copy-only link"}
                                                    {revoked && <span className="ml-2 text-xs text-red-700">revoked</span>}
                                                </div>
                                                <div className="text-xs text-slate-500">Expires {share.expires_at}</div>
                                                {share.note && <div className="text-xs text-slate-600 mt-1">{share.note}</div>}
                                            </div>
                                            <div className="flex gap-2">
                                                {!revoked && (
                                                    <Button type="button" size="sm" variant="outline" onClick={() => copyShareUrl(share)}>
                                                        <Copy size={12} className="mr-1" /> Copy
                                                    </Button>
                                                )}
                                                {!revoked && (
                                                    <Button type="button" size="sm" variant="outline" onClick={() => revokeShareToken(share)}>
                                                        <Trash2 size={12} className="mr-1" /> Revoke
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
